import React, { useState, useEffect } from 'react';
import { 
  Palette, Plus, Edit2, Trash2, Tag, 
  Search, Filter, MoreVertical, Ruler, Layers,
  RefreshCw, AlertCircle
} from 'lucide-react';

const Attributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddAttrModal, setShowAddAttrModal] = useState(false);
  const [showAddValueModal, setShowAddValueModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'select'
  });

  const [valueForm, setValueForm] = useState({
    value: '',
    hex_code: '',
    attribute_id: ''
  });

  const API_URL = 'https://api.maechi.in';

  // Fetch attributes from API
  const fetchAttributes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/attributes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('maechi_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAttributes(data.data);
      } else {
        setError(data.message || 'Failed to load attributes');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  // Create new attribute
  const handleAddAttribute = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/attributes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('maechi_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowAddAttrModal(false);
        setFormData({ name: '', slug: '', type: 'select' });
        fetchAttributes(); // Refresh list
      } else {
        alert(data.message || 'Failed to create attribute');
      }
    } catch (err) {
      console.error('Create error:', err);
      alert('Network error. Please try again.');
    }
  };

  // Add value to attribute
  const handleAddValue = async (e) => {
    e.preventDefault();
    if (!selectedAttribute) return;
    
    try {
      const valueData = {
        attribute_id: selectedAttribute.id,
        value: valueForm.value,
        hex_code: valueForm.hex_code || ''
      };
      
      const response = await fetch(`${API_URL}/attributes?value=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('maechi_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(valueData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowAddValueModal(false);
        setValueForm({ value: '', hex_code: '', attribute_id: '' });
        fetchAttributes(); // Refresh list
      } else {
        alert(data.message || 'Failed to add value');
      }
    } catch (err) {
      console.error('Add value error:', err);
      alert('Network error. Please try again.');
    }
  };

  // Delete attribute
  const handleDeleteAttribute = async (id) => {
    if (!window.confirm('Delete this attribute and all its values?')) return;
    
    try {
      const response = await fetch(`${API_URL}/attributes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('maechi_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchAttributes(); // Refresh list
      } else {
        alert(data.message || 'Failed to delete attribute');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Network error. Please try again.');
    }
  };

  // Delete attribute value
  const handleDeleteValue = async (attrId, valueId) => {
    if (!window.confirm('Delete this value?')) return;
    
    try {
      const response = await fetch(`${API_URL}/attributes/${attrId}?value_id=${valueId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('maechi_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchAttributes(); // Refresh list
      } else {
        alert(data.message || 'Failed to delete value');
      }
    } catch (err) {
      console.error('Delete value error:', err);
      alert('Network error. Please try again.');
    }
  };

  const getIcon = (name) => {
    switch(name.toLowerCase()) {
      case 'color': return <Palette size={20} />;
      case 'size': return <Ruler size={20} />;
      case 'material': return <Layers size={20} />;
      default: return <Tag size={20} />;
    }
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '-');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
        <span className="ml-3 text-gray-600">Loading attributes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attributes</h1>
          <p className="text-gray-600">Manage product attributes ({attributes.length} total)</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchAttributes}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowAddAttrModal(true)}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus size={18} className="mr-2" />
            Add Attribute
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
          <AlertCircle className="text-red-600 mr-3" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg mr-4">
              <Tag className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Attributes</p>
              <p className="text-2xl font-bold text-gray-900">{attributes.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg mr-4">
              <Palette className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Color Values</p>
              <p className="text-2xl font-bold text-gray-900">
                {attributes.find(a => a.name.toLowerCase() === 'color')?.values?.length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg mr-4">
              <Ruler className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Size Values</p>
              <p className="text-2xl font-bold text-gray-900">
                {attributes.find(a => a.name.toLowerCase() === 'size')?.values?.length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-teal-50 rounded-lg mr-4">
              <Layers className="text-teal-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Values</p>
              <p className="text-2xl font-bold text-gray-900">
                {attributes.reduce((sum, attr) => sum + (attr.values?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Attributes Grid */}
      {attributes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Tag className="mx-auto text-gray-300" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mt-4">No attributes found</h3>
          <p className="text-gray-600 mt-2">Create your first attribute to get started</p>
          <button
            onClick={() => setShowAddAttrModal(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Attribute
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {attributes.map((attribute) => (
            <div key={attribute.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-gray-100 rounded-lg mr-3">
                      {getIcon(attribute.name)}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{attribute.name}</h3>
                      <p className="text-gray-500 text-sm">Type: {attribute.type}</p>
                      <p className="text-gray-500 text-xs">Slug: {attribute.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => {
                        setSelectedAttribute(attribute);
                        setValueForm({ ...valueForm, attribute_id: attribute.id });
                        setShowAddValueModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Value
                    </button>
                    <button 
                      onClick={() => handleDeleteAttribute(attribute.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Values ({attribute.values?.length || 0})
                  </h4>
                  <span className="text-xs text-gray-500">
                    {attribute.product_count || 0} products
                  </span>
                </div>
                
                <div className="space-y-3">
                  {attribute.values?.length > 0 ? (
                    attribute.values.map((val) => (
                      <div key={val.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          {attribute.name.toLowerCase() === 'color' && val.hex_code && (
                            <div 
                              className="w-6 h-6 rounded-full border border-gray-300 mr-3"
                              style={{ backgroundColor: val.hex_code }}
                              title={val.hex_code}
                            />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{val.value}</p>
                            {val.hex_code && (
                              <p className="text-gray-500 text-xs">{val.hex_code}</p>
                            )}
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteValue(attribute.id, val.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No values added yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Attribute Modal */}
      {showAddAttrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Add New Attribute</h3>
              <p className="text-gray-600 text-sm mt-1">Create product attribute like Color, Size, etc.</p>
            </div>
            
            <form onSubmit={handleAddAttribute} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attribute Name *
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    });
                  }}
                  placeholder="e.g., Color, Size, Material"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="e.g., color, size"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Input Type
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <option value="select">Dropdown Select</option>
                  <option value="radio">Radio Buttons</option>
                  <option value="checkbox">Checkboxes</option>
                  <option value="text">Text Input</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddAttrModal(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Attribute
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Value Modal */}
      {showAddValueModal && selectedAttribute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Add Value to {selectedAttribute.name}</h3>
              <p className="text-gray-600 text-sm mt-1">Add new option for this attribute</p>
            </div>
            
            <form onSubmit={handleAddValue} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value Name *
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={valueForm.value}
                  onChange={(e) => setValueForm({...valueForm, value: e.target.value})}
                  placeholder={selectedAttribute.name.toLowerCase() === 'color' ? 'e.g., Midnight Black' : 'e.g., Large'}
                />
              </div>
              
              {selectedAttribute.name.toLowerCase() === 'color' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color Hex Code
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={valueForm.hex_code}
                      onChange={(e) => setValueForm({...valueForm, hex_code: e.target.value})}
                      placeholder="#000000"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      title="Enter hex color code (e.g., #FF0000)"
                    />
                    {valueForm.hex_code && (
                      <div 
                        className="w-10 h-10 rounded-lg border border-gray-300"
                        style={{ 
                          backgroundColor: valueForm.hex_code,
                          borderColor: valueForm.hex_code
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddValueModal(false);
                    setSelectedAttribute(null);
                  }}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Value
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attributes;